import './App.css';
import './theme-overrides.css';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { trackPageView, syncConsentFromStorage, markVisitor } from './lib/analytics';
import { ThemeTrialToggle, useThemeTrial } from './lib/themeTrial';

// Eagerly loaded (always-on-screen) — keeps shell light
import Navigation from './components/Navigation';
import ScrollToTop from './components/ScrollToTop';
import FloatingContributeButton from './components/FloatingContributeButton';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import AnnouncementBanner from './components/AnnouncementBanner';
import AIChatWidget from './components/AIChatWidget';
import BackToTopButton from './components/BackToTopButton';
import AuthCallback from './components/AuthCallback';
import CookieBanner from './components/CookieBanner';
import TrialExpiredOverlay from './components/TrialExpiredOverlay';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import BottomNav from './components/BottomNav';
import NPSPrompt from './components/NPSPrompt';
import ReferralUpsellPopup from './components/ReferralUpsellPopup';
import TourController from './components/TourController';
import { TourProvider } from './context/TourContext';
import { Toaster } from './components/ui/sonner';

// Public pages — lazy
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Membership = lazy(() => import('./pages/MembershipV2'));
const MembershipLegacy = lazy(() => import('./pages/Membership'));
const Events = lazy(() => import('./pages/Events'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const Sponsorship = lazy(() => import('./pages/Sponsorship'));
const GlobalOffices = lazy(() => import('./pages/GlobalOffices'));
const SocialResponsibility = lazy(() => import('./pages/SocialResponsibility'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Downloads = lazy(() => import('./pages/Downloads'));
const Blog = lazy(() => import('./pages/Blog'));

// Auth pages — lazy
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const InviteLanding = lazy(() => import('./pages/InviteLanding'));
const TwoFactorPrompt = lazy(() => import('./pages/TwoFactorPrompt'));
const TwoFactorSetupEnforced = lazy(() => import('./pages/TwoFactorSetupEnforced'));

// Chapters
const Chapters = lazy(() => import('./pages/Chapters'));
const ChapterDetail = lazy(() => import('./pages/ChapterDetail'));

// Members
const MemberDirectory = lazy(() => import('./pages/MemberDirectory'));
const MemberDetail = lazy(() => import('./pages/MemberDetail'));

// Module pages
const InvestmentBoard = lazy(() => import('./pages/InvestmentBoard'));
const BusinessDirectory = lazy(() => import('./pages/BusinessDirectory'));
const BusinessDetail = lazy(() => import('./pages/BusinessDetail'));
const InvestorOffers = lazy(() => import('./pages/InvestorOffers'));
const InvestorOfferDetail = lazy(() => import('./pages/InvestorOfferDetail'));
const MemberBenefits = lazy(() => import('./pages/MemberBenefits'));
const BenefitDetail = lazy(() => import('./pages/BenefitDetail'));
const ProviderDirectory = lazy(() => import('./pages/ProviderDirectory'));
const ProviderDetail = lazy(() => import('./pages/ProviderDetail'));

// Dashboard
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CapitalFlowPreview = lazy(() => import('./pages/preview/CapitalFlow'));
const TheVaultPreview = lazy(() => import('./pages/preview/TheVault'));
const MidnightSilkPreview = lazy(() => import('./pages/preview/MidnightSilk'));
const BoardroomPremierPreview = lazy(() => import('./pages/preview/BoardroomPremier'));
const PreviewIndex = lazy(() => import('./pages/preview/Index'));
const ROICalculatorPreview = lazy(() => import('./pages/preview/ROICalculator'));
const PricingRevampPreview = lazy(() => import('./pages/preview/PricingRevamp'));
const Profile = lazy(() => import('./pages/dashboard/Profile'));
const MyApplications = lazy(() => import('./pages/dashboard/MyApplications'));
const Payments = lazy(() => import('./pages/dashboard/Payments'));
const Referrals = lazy(() => import('./pages/dashboard/Referrals'));
const MyConnections = lazy(() => import('./pages/dashboard/MyConnections'));
const MyEventInvites = lazy(() => import('./pages/dashboard/MyEventInvites'));
const MyBusinesses = lazy(() => import('./pages/dashboard/MyBusinesses'));
const MyInvestorOffers = lazy(() => import('./pages/dashboard/MyInvestorOffers'));
const MyBenefits = lazy(() => import('./pages/dashboard/MyBenefits'));
const MyRsvps = lazy(() => import('./pages/dashboard/MyRsvps'));
const MyInvestments = lazy(() => import('./pages/dashboard/MyInvestments'));
const NotificationPreferences = lazy(() => import('./pages/dashboard/NotificationPreferences'));
const ShareTestimonial = lazy(() => import('./pages/dashboard/ShareTestimonial'));
const Subscription = lazy(() => import('./pages/dashboard/Subscription'));
const ReferralHub = lazy(() => import('./pages/dashboard/ReferralHub'));
const Resources = lazy(() => import('./pages/Resources'));
const SEOLandingPage = lazy(() => import('./components/SEOLandingPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const PrivacyRights = lazy(() => import('./pages/PrivacyRights'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const Activity = lazy(() => import('./pages/dashboard/Activity'));
const Progress = lazy(() => import('./pages/dashboard/Progress'));
const Badges = lazy(() => import('./pages/Badges'));
const MentorsDirectory = lazy(() => import('./pages/MentorsDirectory'));
const MentorDetail = lazy(() => import('./pages/MentorDetail'));
const MentorHub = lazy(() => import('./pages/dashboard/MentorHub'));
const JobsDirectory = lazy(() => import('./pages/JobsDirectory'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const PostJob = lazy(() => import('./pages/PostJob'));
const JobsHub = lazy(() => import('./pages/dashboard/JobsHub'));
const PartnersDirectory = lazy(() => import('./pages/PartnersDirectory'));
const PartnerDetail = lazy(() => import('./pages/PartnerDetail'));
const MastermindsDirectory = lazy(() => import('./pages/MastermindsDirectory'));
const MastermindDetail = lazy(() => import('./pages/MastermindDetail'));
const CreateMastermind = lazy(() => import('./pages/CreateMastermind'));
const AdminMagazine = lazy(() => import('./pages/admin/AdminMagazine'));
const DealRoom = lazy(() => import('./pages/DealRoom'));
const DealDetail = lazy(() => import('./pages/DealDetail'));
const RealEstateExit = lazy(() => import('./pages/RealEstateExit'));
const RealEstateExitSubmit = lazy(() => import('./pages/RealEstateExitSubmit'));
const RealEstateExitDeals = lazy(() => import('./pages/RealEstateExitDeals'));
const RealEstateExitFlyer = lazy(() => import('./pages/RealEstateExitFlyer'));
const AdminRealEstateExits = lazy(() => import('./pages/admin/AdminRealEstateExits'));
const CreateDeal = lazy(() => import('./pages/CreateDeal'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const TrustCenter = lazy(() => import('./pages/TrustCenter'));
const NotFound = lazy(() => import('./pages/NotFound'));
const SEOLanding = lazy(() => import('./pages/SEOLanding'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const Messages = lazy(() => import('./pages/Messages'));
const PitchDeckReviewer = lazy(() => import('./pages/PitchDeckReviewer'));

// Admin
const AdminHub = lazy(() => import('./pages/admin/AdminHub'));
const AdminApplications = lazy(() => import('./pages/admin/AdminApplications'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminContent = lazy(() => import('./pages/admin/AdminContent'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments'));
const AdminSponsorships = lazy(() => import('./pages/admin/AdminSponsorships'));
const AdminEvents = lazy(() => import('./pages/admin/AdminEvents'));
const AdminScan = lazy(() => import('./pages/admin/AdminScan'));
const AdminNewsletters = lazy(() => import('./pages/admin/AdminNewsletters'));
const AdminPrelaunchWaitlist = lazy(() => import('./pages/admin/AdminPrelaunchWaitlist'));
const AdminFunding = lazy(() => import('./pages/admin/AdminFunding'));
const AdminMarketingCreatives = lazy(() => import('./pages/admin/AdminMarketingCreatives'));
const AdminPressCoverage = lazy(() => import('./pages/admin/AdminPressCoverage'));
const AdminGovBenefits = lazy(() => import('./pages/admin/AdminGovBenefits'));
const AdminSocialMediaKit = lazy(() => import('./pages/admin/AdminSocialMediaKit'));
const IBPointsPage = lazy(() => import('./pages/IBPointsPage'));
const Clubs = lazy(() => import('./pages/Clubs'));
const ServicesHub = lazy(() => import('./pages/Services').then((m) => ({ default: m.ServicesHub })));
const ServicesCategory = lazy(() => import('./pages/Services').then((m) => ({ default: m.ServicesCategory })));
const ServicesSubcategory = lazy(() => import('./pages/Services').then((m) => ({ default: m.ServicesSubcategory })));
const AdminServiceRequests = lazy(() => import('./pages/admin/AdminServiceRequests'));
const ServiceConcierge = lazy(() => import('./pages/ServiceConcierge'));
const ThemePreview = lazy(() => import('./pages/ThemePreview'));
const PromoVideoPreview = lazy(() => import('./pages/PromoVideoPreview'));
const ServiceProviderApply = lazy(() => import('./pages/ServiceProviderApply'));
const ServiceProviderDashboard = lazy(() => import('./pages/ServiceProviderDashboard'));
const ServiceProviderPublic = lazy(() => import('./pages/ServiceProviderPublic'));
const AdminServiceProviders = lazy(() => import('./pages/admin/AdminServiceProviders'));
const GovBenefits = lazy(() => import('./pages/GovBenefits'));
const GovBenefitDetail = lazy(() => import('./pages/GovBenefitDetail'));
const GovBenefitsEligibility = lazy(() => import('./pages/GovBenefitsEligibility'));
const GovBenefitsMine = lazy(() => import('./pages/GovBenefitsMine'));
const FundingHub = lazy(() => import('./pages/FundingHub'));
const FundingDetail = lazy(() => import('./pages/FundingDetail'));
const Press = lazy(() => import('./pages/Press'));
const LenderPortalLogin = lazy(() => import('./pages/lender/LenderPortalLogin'));
const LenderPortalVerify = lazy(() => import('./pages/lender/LenderPortalVerify'));
const LenderPortalDashboard = lazy(() => import('./pages/lender/LenderPortalDashboard').then(m => ({ default: m.LenderPortalDashboard })));
const LenderLeadDetail = lazy(() => import('./pages/lender/LenderPortalDashboard').then(m => ({ default: m.LenderLeadDetail })));
const AdminModeration = lazy(() => import('./pages/admin/AdminModeration'));
const AdminChatSessions = lazy(() => import('./pages/admin/AdminChatSessions'));
const AdminMatchNotifications = lazy(() => import('./pages/admin/AdminMatchNotifications'));
const AdminAuditLog = lazy(() => import('./pages/admin/AdminAuditLog'));
const AdminRoles = lazy(() => import('./pages/admin/AdminRoles'));
const AdminChapters = lazy(() => import('./pages/admin/AdminChapters'));
const AdminTestimonials = lazy(() => import('./pages/admin/AdminTestimonials'));
const AdminDnsValidator = lazy(() => import('./pages/admin/AdminDnsValidator'));
const AdminWhatsApp = lazy(() => import('./pages/admin/AdminWhatsApp'));
const AdminEmail = lazy(() => import('./pages/admin/AdminEmail'));
const AdminSponsorshipPackages = lazy(() => import('./pages/admin/AdminSponsorshipPackages'));
const AdminSponsorsShowcase = lazy(() => import('./pages/admin/AdminSponsorsShowcase'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminBlogEditor = lazy(() => import('./pages/admin/AdminBlogEditor'));
const Communities = lazy(() => import('./pages/Communities'));
const CommunityDetail = lazy(() => import('./pages/CommunityDetail'));
const AdminCommunities = lazy(() => import('./pages/admin/AdminCommunities'));
const AdminDownloads = lazy(() => import('./pages/admin/AdminDownloads'));
const Impact = lazy(() => import('./pages/Impact'));
const ImpactNew = lazy(() => import('./pages/ImpactNew'));
const ImpactDetail = lazy(() => import('./pages/ImpactDetail'));
const ImpactLeaderboard = lazy(() => import('./pages/ImpactLeaderboard'));
const ImpactMap = lazy(() => import('./pages/ImpactMap'));
const AdminImpact = lazy(() => import('./pages/admin/AdminImpact'));
const AdminFAQ = lazy(() => import('./pages/admin/AdminFAQ'));
const AdminPageContent = lazy(() => import('./pages/admin/AdminPageContent'));
const AdminTrials = lazy(() => import('./pages/admin/AdminTrials'));
const AdminGrowth = lazy(() => import('./pages/admin/AdminGrowth'));
const AdminPayoutQueue = lazy(() => import('./pages/admin/AdminPayoutQueue'));
const AdminMemberControl = lazy(() => import('./pages/admin/AdminMemberControl'));
const AdminAssignments = lazy(() => import('./pages/admin/AdminAssignments'));
const AdminReferralAnalytics = lazy(() => import('./pages/admin/AdminReferralAnalytics'));

// Generic route-level fallback — small, dark, gold-spinner consistent with app
const RouteFallback = () => (
  <div className="min-h-screen pt-32 flex items-center justify-center" data-testid="route-fallback">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-slate-400 text-sm">Loading…</p>
    </div>
  </div>
);

function AppRouter() {
  const location = useLocation();

  // Site-wide theme trial — reads localStorage on mount, applies data-theme
  // attribute to <html>, and keeps in sync as the toggle pill flips it.
  useThemeTrial();

  // GA4 — re-fire page_view on every SPA route change; honor stored consent
  // (CookieBanner persisted analytics opt-in/out to localStorage).
  useEffect(() => {
    syncConsentFromStorage();
    markVisitor();   // pushes is_returning_visitor to dataLayer once per load
  }, []);
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // CRITICAL: Detect session_id synchronously to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const isPreview = location.pathname === '/preview' || location.pathname.startsWith('/preview/');
  const isLenderPortal = location.pathname === '/lender-portal' || location.pathname.startsWith('/lender-portal/');
  const hideMemberChrome = isPreview || isLenderPortal;

  return (
    <>
      <ScrollToTop />
      {!hideMemberChrome && <FloatingContributeButton />}
      {!hideMemberChrome && <Navigation />}
      {!hideMemberChrome && <TourController />}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/investor-club-india"   element={<SEOLandingPage slug="investor-club-india" />} />
          <Route path="/founder-network-mumbai" element={<SEOLandingPage slug="founder-network-mumbai" />} />
          <Route path="/family-office-india"    element={<SEOLandingPage slug="family-office-india" />} />

          {/* Programmatic SEO landing pages — auto-generated city × sector pages */}
          <Route path="/investors/:city" element={<SEOLanding kind="city" />} />
          <Route path="/founders/:sector" element={<SEOLanding kind="sector" />} />
          <Route path="/network/:city/:sector" element={<SEOLanding kind="city_sector" />} />

          {/* Public, indexable member profile */}
          <Route path="/u/:slug" element={<PublicProfile />} />

          {/* In-app messages (Premium+ to send) */}
          <Route path="/dashboard/messages" element={<Messages />} />

          {/* Public AI pitch-deck reviewer (lead magnet) */}
          <Route path="/tools/pitch-deck-review" element={<PitchDeckReviewer />} />
          <Route path="/preview" element={<PreviewIndex />} />
          <Route path="/preview/capital-flow" element={<CapitalFlowPreview />} />
          <Route path="/preview/the-vault" element={<TheVaultPreview />} />
          <Route path="/preview/midnight-silk" element={<MidnightSilkPreview />} />
          <Route path="/preview/boardroom-premier" element={<BoardroomPremierPreview />} />
          <Route path="/preview/roi-calculator"    element={<ROICalculatorPreview />} />
          <Route path="/preview/pricing-revamp"    element={<PricingRevampPreview />} />
          <Route path="/about" element={<About />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/membership-v1" element={<MembershipLegacy />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/:eventId" element={<EventDetail />} />
          <Route path="/sponsorship" element={<Sponsorship />} />
          <Route path="/global-offices" element={<GlobalOffices />} />
          <Route path="/business-showcase" element={<BusinessDirectory />} />
          <Route path="/social-responsibility" element={<SocialResponsibility />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/invite/:code" element={<InviteLanding />} />
          <Route path="/two-factor" element={<TwoFactorPrompt />} />
          <Route path="/two-factor-setup" element={<TwoFactorSetupEnforced />} />

          {/* Chapters */}
          <Route path="/chapters" element={<Chapters />} />
          <Route path="/chapters/:chapterId" element={<ChapterDetail />} />
          <Route path="/city-chapters" element={<Chapters />} />

          {/* Content */}
          <Route path="/faq" element={<FAQ />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Communities (Phase 48) */}
          <Route path="/communities" element={<Communities />} />
          <Route path="/communities/:slug" element={<CommunityDetail />} />

          {/* Social Impact Hub (Phase 50) */}
          <Route path="/impact" element={<Impact />} />
          <Route path="/impact/new" element={<ImpactNew />} />
          <Route path="/impact/leaderboard" element={<ImpactLeaderboard />} />
          <Route path="/impact/map" element={<ImpactMap />} />
          <Route path="/impact/:slug" element={<ImpactDetail />} />

          {/* Members & Modules */}
          <Route path="/members" element={<MemberDirectory />} />
          <Route path="/members/:userId" element={<MemberDetail />} />
          <Route path="/investments" element={<InvestmentBoard />} />
          <Route path="/real-estate-exit" element={<RealEstateExit />} />
          <Route path="/real-estate-exit/submit" element={<RealEstateExitSubmit />} />
          <Route path="/real-estate-exit/deals" element={<RealEstateExitDeals />} />
          <Route path="/real-estate-exit/flyer" element={<RealEstateExitFlyer />} />
          <Route path="/business-directory" element={<BusinessDirectory />} />
          {/* Common plural alias — /businesses (without ID) should also work */}
          <Route path="/businesses" element={<Navigate to="/business-directory" replace />} />
          <Route path="/businesses/:businessId" element={<BusinessDetail />} />
          <Route path="/investor-offers" element={<InvestorOffers />} />
          <Route path="/investor-offers/:offerId" element={<InvestorOfferDetail />} />
          <Route path="/benefits" element={<MemberBenefits />} />
          <Route path="/benefits/providers" element={<ProviderDirectory />} />
          <Route path="/benefits/provider/:userId" element={<ProviderDetail />} />
          <Route path="/benefits/:benefitId" element={<BenefitDetail />} />
          <Route path="/member-benefits" element={<MemberBenefits />} />
          <Route path="/member-benefits/providers" element={<ProviderDirectory />} />
          <Route path="/member-benefits/provider/:userId" element={<ProviderDetail />} />
          <Route path="/member-benefits/:benefitId" element={<BenefitDetail />} />

          {/* Dashboard Routes */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/profile" element={<Profile />} />
          <Route path="/dashboard/activity" element={<Activity />} />
          <Route path="/dashboard/connections" element={<MyConnections />} />
          <Route path="/dashboard/event-invites" element={<MyEventInvites />} />
          <Route path="/dashboard/applications" element={<MyApplications />} />
          <Route path="/dashboard/payments" element={<Payments />} />
          <Route path="/dashboard/referrals" element={<Referrals />} />
          <Route path="/dashboard/my-businesses" element={<MyBusinesses />} />
          <Route path="/dashboard/investor-offers" element={<MyInvestorOffers />} />
          <Route path="/dashboard/my-benefits" element={<MyBenefits />} />
          <Route path="/dashboard/my-rsvps" element={<MyRsvps />} />
          <Route path="/dashboard/my-investments" element={<MyInvestments />} />
          <Route path="/dashboard/settings/notifications" element={<NotificationPreferences />} />
          <Route path="/dashboard/share-testimonial" element={<ShareTestimonial />} />
          <Route path="/dashboard/subscription" element={<Subscription />} />
          <Route path="/dashboard/refer" element={<ReferralHub />} />
          <Route path="/dashboard/progress" element={<Progress />} />
          <Route path="/dashboard/mentor-hub" element={<MentorHub />} />
          <Route path="/badges" element={<Badges />} />
          <Route path="/mentors" element={<MentorsDirectory />} />
          <Route path="/mentors/:userId" element={<MentorDetail />} />
          <Route path="/jobs" element={<JobsDirectory />} />
          <Route path="/jobs/post" element={<PostJob />} />
          <Route path="/jobs/:jobId" element={<JobDetail />} />
          <Route path="/dashboard/jobs" element={<JobsHub />} />
          <Route path="/partners" element={<PartnersDirectory />} />
          <Route path="/partners/:partnerId" element={<PartnerDetail />} />
          <Route path="/masterminds" element={<MastermindsDirectory />} />
          <Route path="/masterminds/new" element={<CreateMastermind />} />
          <Route path="/masterminds/:id" element={<MastermindDetail />} />
          <Route path="/admin/magazine" element={<AdminMagazine />} />
          <Route path="/deal-room" element={<DealRoom />} />
          <Route path="/deal-room/new" element={<CreateDeal />} />
          <Route path="/deal-room/:dealId" element={<DealDetail />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy-rights" element={<PrivacyRights />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/trust" element={<TrustCenter />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminHub />} />
          <Route path="/admin/applications" element={<AdminApplications />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/content" element={<AdminContent />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/payments" element={<AdminPayments />} />
          <Route path="/admin/sponsorships" element={<AdminSponsorships />} />
          <Route path="/admin/events" element={<AdminEvents />} />
          <Route path="/admin/scan" element={<AdminScan />} />
          <Route path="/admin/newsletters" element={<AdminNewsletters />} />
          <Route path="/admin/prelaunch-waitlist" element={<AdminPrelaunchWaitlist />} />
          <Route path="/admin/funding" element={<AdminFunding />} />
          <Route path="/admin/marketing-creatives" element={<AdminMarketingCreatives />} />
          <Route path="/admin/press-coverage" element={<AdminPressCoverage />} />
          <Route path="/admin/gov-benefits" element={<AdminGovBenefits />} />
          <Route path="/admin/social-media-kit" element={<AdminSocialMediaKit />} />
          <Route path="/gov-benefits" element={<GovBenefits />} />
          <Route path="/gov-benefits/state/:state" element={<GovBenefits />} />
          <Route path="/gov-benefits/sector/:sector" element={<GovBenefits />} />
          <Route path="/gov-benefits/:slug" element={<GovBenefitDetail />} />
          <Route path="/dashboard/gov-benefits" element={<GovBenefitsMine />} />
          <Route path="/dashboard/gov-benefits/eligibility" element={<GovBenefitsEligibility />} />
          <Route path="/dashboard/points" element={<IBPointsPage />} />
          <Route path="/clubs" element={<Clubs />} />
          <Route path="/services" element={<ServicesHub />} />
          <Route path="/services/concierge" element={<ServiceConcierge />} />
          {/* Internal design playground — read-only theme trial, noindex */}
          <Route path="/theme-preview" element={<ThemePreview />} />
          {/* Hinglish promo video kit preview (Phase 60.7) */}
          <Route path="/marketing/promo-preview" element={<PromoVideoPreview />} />
          <Route path="/services/sub/:sub_slug" element={<ServicesSubcategory />} />
          <Route path="/services/:category_key" element={<ServicesCategory />} />
          <Route path="/services-experts/:provider_id" element={<ServiceProviderPublic />} />
          <Route path="/dashboard/provider" element={<ServiceProviderDashboard />} />
          <Route path="/dashboard/provider/apply" element={<ServiceProviderApply />} />
          <Route path="/admin/service-requests" element={<AdminServiceRequests />} />
          <Route path="/admin/real-estate-exits" element={<AdminRealEstateExits />} />
          <Route path="/admin/service-providers" element={<AdminServiceProviders />} />
          <Route path="/dashboard/funding" element={<FundingHub />} />
          <Route path="/dashboard/funding/:loanId" element={<FundingDetail />} />
          <Route path="/press" element={<Press />} />
          <Route path="/lender-portal" element={<LenderPortalLogin />} />
          <Route path="/lender-portal/verify" element={<LenderPortalVerify />} />
          <Route path="/lender-portal/dashboard" element={<LenderPortalDashboard />} />
          <Route path="/lender-portal/leads/:leadId" element={<LenderLeadDetail />} />
          <Route path="/admin/moderation" element={<AdminModeration />} />
          <Route path="/admin/chat-sessions" element={<AdminChatSessions />} />
          <Route path="/admin/match-notifications" element={<AdminMatchNotifications />} />
          <Route path="/admin/audit-log" element={<AdminAuditLog />} />
          <Route path="/admin/roles" element={<AdminRoles />} />
          <Route path="/admin/member-control" element={<AdminMemberControl />} />
          <Route path="/admin/assignments" element={<AdminAssignments />} />
          <Route path="/admin/referral-analytics" element={<AdminReferralAnalytics />} />
          <Route path="/admin/chapters" element={<AdminChapters />} />
          <Route path="/admin/testimonials" element={<AdminTestimonials />} />
          <Route path="/admin/dns" element={<AdminDnsValidator />} />
          <Route path="/admin/whatsapp" element={<AdminWhatsApp />} />
          <Route path="/admin/email" element={<AdminEmail />} />
          <Route path="/admin/sponsorship-packages" element={<AdminSponsorshipPackages />} />
          <Route path="/admin/sponsors-showcase" element={<AdminSponsorsShowcase />} />
          <Route path="/admin/blog" element={<AdminBlog />} />
          <Route path="/admin/blog/new" element={<AdminBlogEditor />} />
          <Route path="/admin/blog/edit/:id" element={<AdminBlogEditor />} />
          <Route path="/admin/communities" element={<AdminCommunities />} />
          <Route path="/admin/downloads" element={<AdminDownloads />} />
          <Route path="/admin/impact-issues" element={<AdminImpact />} />
          <Route path="/admin/faq" element={<AdminFAQ />} />
          <Route path="/admin/page-content" element={<AdminPageContent />} />
          <Route path="/admin/trials" element={<AdminTrials />} />
          <Route path="/admin/growth" element={<AdminGrowth />} />
          <Route path="/admin/referrals/payouts" element={<AdminPayoutQueue />} />
          {/* 404 catch-all — must be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!hideMemberChrome && <Footer />}
      {!hideMemberChrome && <WhatsAppButton />}
      {!hideMemberChrome && <AIChatWidget />}
      {!hideMemberChrome && <BackToTopButton />}
      {!hideMemberChrome && <AnnouncementBanner />}
      {!hideMemberChrome && <CookieBanner />}
      {!hideMemberChrome && <TrialExpiredOverlay />}
      {!hideMemberChrome && <PWAInstallPrompt />}
      {!hideMemberChrome && <BottomNav user={typeof window !== 'undefined' && localStorage.getItem('token') ? { authed: true } : null} />}
      {!hideMemberChrome && <NPSPrompt />}
      {!hideMemberChrome && <ReferralUpsellPopup />}
      <ThemeTrialToggle />
      <Toaster position="top-center" />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <TourProvider>
          <AppRouter />
        </TourProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
